<script setup lang="ts">
import { getBackwardsCompatibleNumberFormat } from "mws-utils/lib";

import useActiveCurrency from "~/composables/useActiveCurrency";

import BaseNumericInput from "./BaseNumericInput.vue";

type Props = {
  valid?: boolean;
  name?: string;
  innerClass?: string;
  outerClass?: string;
};

const { valid = true, outerClass = "", innerClass = "" } = defineProps<Props>();
const amount = defineModel<number>();

const { activeCurrency } = useActiveCurrency();

const { locale } = useI18n();

type CurrencyType = {
  position: "prefix" | "suffix";
  symbol: string;
};

const currency = computed<CurrencyType | undefined>(() => {
  const numberAmount = amount.value;
  if (numberAmount && isNaN(numberAmount)) return;

  const currencyParts = getBackwardsCompatibleNumberFormat(locale.value, {
    style: "currency",
    currency: activeCurrency.value.isoCurrencyCode,
    currencyDisplay: "narrowSymbol",
  }).formatToParts(numberAmount);

  return {
    position: currencyParts?.[0]?.type === "currency" ? "prefix" : "suffix",
    symbol:
      currencyParts.find((part) => part.type === "currency")?.value ??
      activeCurrency.value.isoCurrencyCode,
  };
});
</script>

<template>
  <BaseNumericInput
    v-model="amount"
    :name="name"
    :outer-class="
      currency?.position
        ? `w-full currency-input currency-input--${currency.position} ${outerClass}`
        : `w-full currency-input ${outerClass}`
    "
    :inner-class="
      !valid ? `${innerClass} !border-platform-warning` : innerClass
    "
  >
    <template
      v-if="currency?.position === 'prefix'"
      #prefix
    >
      <div
        class="self-start min-w-fit py-4 pl-4"
        data-test="currency-input-prefix"
      >
        {{ currency.symbol }}
      </div>
    </template>
    <template
      v-if="currency?.position === 'suffix'"
      #suffix
    >
      <div
        class="self-start min-w-fit py-4 pr-2"
        data-test="currency-input-suffix"
      >
        {{ currency.symbol }}
      </div>
    </template>
  </BaseNumericInput>
</template>

<style lang="postcss" scoped>
.currency-input {
  @apply w-full;

  &:deep(input) {
    &::placeholder {
      @apply font-normal;
    }
  }

  &--prefix :deep(input) {
    @apply pl-2;
  }

  &--suffix:deep(input) {
    @apply pr-2;
  }
}
</style>
